/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Heading, VStack } from 'chakra-v3';
import { Text } from '../Text';
import HelpOrDivider from './HelpOrDivider';
import HelpWhatsAppLink from './HelpWhatsAppLink';

interface HelpInfoSectionProps {
  whatsappWebUrl?: string;
}

/**
 * HelpInfoSection component displays information about scanning the QR code
 * and provides a link to WhatsApp Web. This section includes a heading,
 * descriptive text, a divider, and the WhatsApp Web link.
 *
 * @param whatsappWebUrl - Optional WhatsApp Web URL to pass to the HelpWhatsAppLink component
 *
 * @example
 * ```tsx
 * <HelpInfoSection whatsappWebUrl="https://web.whatsapp.com" />
 * ```
 */
const HelpInfoSection: React.FC<HelpInfoSectionProps> = ({ whatsappWebUrl }) => (
  <Box w={{ base: '100%', md: '100%', lg: '282px' }} maxW={{ base: '100%', md: '100%', lg: '282px' }}>
    <VStack gap={{ base: 4, md: 6, lg: '24px' }} alignItems="flex-start" w="100%">
      <VStack gap={{ base: 2, md: 3, lg: '12px' }} alignItems="flex-start" w="100%">
        <Heading
          as="h2"
          fontSize={{ base: '20px', md: '22px', lg: '24px' }}
          fontWeight="500"
          fontFamily="subheading"
          color="text.primary"
          lineHeight="1.4"
          letterSpacing="0.06px"
        >
          Scan the QR code
        </Heading>
        <Text variant="body" color="text.primary" fontSize="16px" lineHeight="1.4" letterSpacing="0.08px">
          Our WhatsApp chatbot is available 24/7 to assist you with any issues or questions you may have.
        </Text>
      </VStack>
      <HelpOrDivider />
      <HelpWhatsAppLink whatsappWebUrl={whatsappWebUrl} />
    </VStack>
  </Box>
);

export default HelpInfoSection;
